import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';
import {
  Clock, Calendar, User, Phone, Mail, Plus, Trash2, Edit2,
  CheckCircle, XCircle, AlertCircle, RefreshCw, Shield,
  Sun, Moon, Users
} from 'lucide-react';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const OnCallSchedule = () => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [currentOnCall, setCurrentOnCall] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    userId: '',
    role: 'on_call_clinician',
    priority: 1,
    escalationLevels: ['initial'],
    notificationChannels: ['email', 'push'],
    isAlwaysOnCall: false,
    shifts: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [contactsRes, onCallRes, usersRes] = await Promise.all([
        api.get('/escalation/contacts'),
        api.get('/escalation/on-call'),
        api.get('/admin/doctors?status=ALL') // Get doctors for selection
      ]);

      if (contactsRes.data?.success) {
        setContacts(contactsRes.data.data);
      }
      if (onCallRes.data?.success) {
        setCurrentOnCall(onCallRes.data.data);
      }
      if (usersRes.data?.data) {
        setAvailableUsers(usersRes.data.data || []);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      toast.error('Failed to load schedule data');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedContacts = async () => {
    const confirmed = window.confirm(
      'This will create escalation contacts from all approved doctors in the system.\n\nExisting contacts will not be removed. Continue?'
    );
    if (!confirmed) return;
    try {
      const res = await api.post('/escalation/contacts/seed');
      if (res.data?.success) {
        toast.success(`Created ${res.data.data.length} escalation contacts from approved doctors`);
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to import contacts');
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/escalation/contacts', formData);
      if (res.data?.success) {
        toast.success('Contact added successfully');
        setShowAddModal(false);
        setFormData({
          userId: '',
          role: 'on_call_clinician',
          priority: 1,
          escalationLevels: ['initial'],
          notificationChannels: ['email', 'push'],
          isAlwaysOnCall: false,
          shifts: []
        });
        loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add contact');
    }
  };

  const handleUpdateSchedule = async (contactId, schedule) => {
    try {
      const res = await api.put(`/escalation/contacts/${contactId}`, { schedule });
      if (res.data?.success) {
        toast.success('Schedule updated');
        loadData();
      }
    } catch (err) {
      toast.error('Failed to update schedule');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!confirm('Are you sure you want to remove this contact?')) return;
    
    try {
      const res = await api.delete(`/escalation/contacts/${contactId}`);
      if (res.data?.success) {
        toast.success('Contact removed');
        loadData();
      }
    } catch (err) {
      toast.error('Failed to remove contact');
    }
  };

  const addShift = () => {
    setFormData(prev => ({
      ...prev,
      shifts: [...prev.shifts, { dayOfWeek: 1, startTime: '09:00', endTime: '17:00' }]
    }));
  };

  const updateShift = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      shifts: prev.shifts.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const removeShift = (index) => {
    setFormData(prev => ({
      ...prev,
      shifts: prev.shifts.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-slate-300 animate-spin" />
      </div>
    );
  }

  const onCallContacts = contacts.filter(c => c.role === 'on_call_clinician');
  const otherContacts = contacts.filter(c => c.role !== 'on_call_clinician');

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded uppercase tracking-wider">
            Admin
          </span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-headline text-4xl font-extrabold text-primary tracking-tight">
              On-Call Schedule
            </h2>
            <p className="text-slate-500 mt-1">
              Manage escalation contacts and on-call rotations
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSeedContacts}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl text-sm font-bold hover:bg-slate-200 transition-all flex items-center gap-2"
            >
              <Users className="w-4 h-4" />
              Auto-Seed from Doctors
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-bold hover:bg-primary/90 transition-all flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Contact
            </button>
          </div>
        </div>
      </div>

      {/* Current On-Call Card */}
      <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-3xl p-8 border border-primary/10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Clock className="w-5 h-5 text-primary" />
          </div>
          <h3 className="font-bold text-lg text-slate-900">Currently On-Call</h3>
        </div>
        
        {currentOnCall ? (
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center text-2xl font-bold text-primary shadow-sm">
              {(currentOnCall.userId?.name || currentOnCall.userId?.email || '?')
                .split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </div>
            <div>
              <h4 className="font-bold text-xl text-slate-900">{currentOnCall.userId?.name || currentOnCall.userId?.email || 'Unknown'}</h4>
              <p className="text-sm text-slate-500">{currentOnCall.userId?.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                  currentOnCall.schedule?.isAlwaysOnCall 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {currentOnCall.schedule?.isAlwaysOnCall ? '24/7 On-Call' : 'Scheduled Shifts'}
                </span>
                <span className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-bold">
                  Priority {currentOnCall.priority}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 text-amber-600">
            <AlertCircle className="w-5 h-5" />
            <p className="font-medium">No one is currently on-call. Set up on-call schedule.</p>
          </div>
        )}
      </div>

      {/* On-Call Clinicians */}
      <div className="bg-white rounded-3xl p-6 shadow-sm border border-outline-variant/10">
        <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          On-Call Clinicians ({onCallContacts.length})
        </h3>
        
        {onCallContacts.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <p>No on-call clinicians configured</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-2 text-primary font-bold text-sm hover:underline"
            >
              Add your first on-call clinician
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {onCallContacts.map(contact => (
              <ContactCard
                key={contact._id}
                contact={contact}
                onUpdateSchedule={handleUpdateSchedule}
                onDelete={handleDeleteContact}
              />
            ))}
          </div>
        )}
      </div>

      {/* Other Escalation Contacts */}
      {otherContacts.length > 0 && (
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-outline-variant/10">
          <h3 className="font-bold text-lg text-slate-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-secondary" />
            Other Escalation Contacts ({otherContacts.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {otherContacts.map(contact => (
              <div key={contact._id} className="p-4 rounded-xl bg-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center font-bold text-slate-500">
                    {(contact.userId?.name || contact.userId?.email || '?')[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p className="font-bold text-sm text-slate-900">{contact.userId?.name || contact.userId?.email || 'Unknown'}</p>
                    <p className="text-xs text-slate-500 capitalize">{contact.role.replace(/_/g, ' ')}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteContact(contact._id)}
                  className="p-2 text-slate-500 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Contact Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-bold text-xl text-slate-900">Add Escalation Contact</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-500 hover:text-slate-600">
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleAddContact} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">User</label>
                <select
                  value={formData.userId}
                  onChange={e => setFormData(prev => ({ ...prev, userId: e.target.value }))}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  required
                >
                  <option value="">Select a doctor...</option>
                  {availableUsers.map(user => (
                    <option key={user._id} value={user._id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={e => setFormData(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                >
                  <option value="on_call_clinician">On-Call Clinician</option>
                  <option value="care_manager">Care Manager</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="emergency_contact">Emergency Contact</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">Priority</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.priority}
                  onChange={e => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                />
              </div>

              {formData.role === 'on_call_clinician' && (
                <>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                    <input
                      type="checkbox"
                      id="alwaysOnCall"
                      checked={formData.isAlwaysOnCall}
                      onChange={e => setFormData(prev => ({ ...prev, isAlwaysOnCall: e.target.checked }))}
                      className="w-5 h-5 rounded border-slate-300 text-primary focus:ring-primary"
                    />
                    <label htmlFor="alwaysOnCall" className="font-bold text-sm text-slate-700">
                      Always on-call (24/7)
                    </label>
                  </div>

                  {!formData.isAlwaysOnCall && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-bold text-slate-700">Weekly Shifts</label>
                        <button
                          type="button"
                          onClick={addShift}
                          className="text-sm text-primary font-bold hover:underline flex items-center gap-1"
                        >
                          <Plus className="w-4 h-4" /> Add Shift
                        </button>
                      </div>
                      <div className="space-y-2">
                        {formData.shifts.map((shift, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50">
                            <select
                              value={shift.dayOfWeek}
                              onChange={e => updateShift(idx, 'dayOfWeek', parseInt(e.target.value))}
                              className="flex-1 px-2 py-1 rounded border border-slate-200 text-sm"
                            >
                              {DAYS.map((day, i) => (
                                <option key={i} value={i}>{day}</option>
                              ))}
                            </select>
                            <input
                              type="time"
                              value={shift.startTime}
                              onChange={e => updateShift(idx, 'startTime', e.target.value)}
                              className="px-2 py-1 rounded border border-slate-200 text-sm w-24"
                            />
                            <span className="text-slate-500">to</span>
                            <input
                              type="time"
                              value={shift.endTime}
                              onChange={e => updateShift(idx, 'endTime', e.target.value)}
                              className="px-2 py-1 rounded border border-slate-200 text-sm w-24"
                            />
                            <button
                              type="button"
                              onClick={() => removeShift(idx)}
                              className="p-1 text-slate-500 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 transition-all"
                >
                  Add Contact
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Contact Card Component
const ContactCard = ({ contact, onUpdateSchedule, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [schedule, setSchedule] = useState({
    isAlwaysOnCall: contact.schedule?.isAlwaysOnCall || false,
    shifts: contact.schedule?.shifts || []
  });

  const handleSave = () => {
    onUpdateSchedule(contact._id, schedule);
    setIsEditing(false);
  };

  const addShift = () => {
    setSchedule(prev => ({
      ...prev,
      shifts: [...prev.shifts, { dayOfWeek: 1, startTime: '09:00', endTime: '17:00' }]
    }));
  };

  const updateShift = (index, field, value) => {
    setSchedule(prev => ({
      ...prev,
      shifts: prev.shifts.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const removeShift = (index) => {
    setSchedule(prev => ({
      ...prev,
      shifts: prev.shifts.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="p-4 rounded-2xl bg-slate-50">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center font-bold text-primary text-lg shadow-sm">
            {(contact.userId?.name || contact.userId?.email || '?')
              .split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
          </div>
          <div>
            <p className="font-bold text-slate-900">{contact.userId?.name || contact.userId?.email || 'Unknown'}</p>
            <p className="text-sm text-slate-500">{contact.userId?.email}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-bold">
                Priority {contact.priority}
              </span>
              {contact.isActive ? (
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-bold flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Active
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded text-xs font-bold">
                  Inactive
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="p-2 text-slate-500 hover:text-primary transition-colors rounded-lg hover:bg-primary/10"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(contact._id)}
            className="p-2 text-slate-500 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Schedule Display / Edit */}
      <div className="mt-4 pt-4 border-t border-slate-200">
        {isEditing ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id={`always-${contact._id}`}
                checked={schedule.isAlwaysOnCall}
                onChange={e => setSchedule(prev => ({ ...prev, isAlwaysOnCall: e.target.checked }))}
                className="w-5 h-5 rounded border-slate-300 text-primary focus:ring-primary"
              />
              <label htmlFor={`always-${contact._id}`} className="font-bold text-sm text-slate-700">
                Always on-call (24/7)
              </label>
            </div>

            {!schedule.isAlwaysOnCall && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-slate-700">Weekly Shifts</span>
                  <button
                    onClick={addShift}
                    className="text-sm text-primary font-bold hover:underline flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Add
                  </button>
                </div>
                {schedule.shifts.map((shift, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <select
                      value={shift.dayOfWeek}
                      onChange={e => updateShift(idx, 'dayOfWeek', parseInt(e.target.value))}
                      className="flex-1 px-2 py-1 rounded border border-slate-200 text-sm"
                    >
                      {DAYS.map((day, i) => (
                        <option key={i} value={i}>{day}</option>
                      ))}
                    </select>
                    <input
                      type="time"
                      value={shift.startTime}
                      onChange={e => updateShift(idx, 'startTime', e.target.value)}
                      className="px-2 py-1 rounded border border-slate-200 text-sm w-20"
                    />
                    <span className="text-slate-500">-</span>
                    <input
                      type="time"
                      value={shift.endTime}
                      onChange={e => updateShift(idx, 'endTime', e.target.value)}
                      className="px-2 py-1 rounded border border-slate-200 text-sm w-20"
                    />
                    <button
                      onClick={() => removeShift(idx)}
                      className="p-1 text-slate-500 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setIsEditing(false)}
                className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded-lg text-sm font-bold hover:bg-slate-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-3 py-1.5 bg-primary text-white rounded-lg text-sm font-bold hover:bg-primary/90"
              >
                Save Schedule
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4 text-slate-500" />
            {contact.schedule?.isAlwaysOnCall ? (
              <span className="text-emerald-600 font-bold">24/7 Always On-Call</span>
            ) : contact.schedule?.shifts?.length > 0 ? (
              <span className="text-slate-600">
                {contact.schedule.shifts.length} scheduled shift{contact.schedule.shifts.length !== 1 ? 's' : ''}
              </span>
            ) : (
              <span className="text-amber-600">No schedule set</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OnCallSchedule;
